import type { TOC } from '@ember/component/template-only'
import type ManageProgressRoute from 'teacher-ui-client/manage/progress/route'
import type ManageProgressController from 'teacher-ui-client/manage/progress/controller'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { t } from 'ember-intl'
import { LessonGuideTableReading } from 'teacher-ui-client/components/tables/lesson-guide-table/reading'
import { LessonGuideTableFastPhonics } from 'teacher-ui-client/components/tables/lesson-guide-table/fast-phonics'
import { LessonGuideTableMaths } from 'teacher-ui-client/components/tables/lesson-guide-table/maths'
import { LessonGuideTableWriting } from 'teacher-ui-client/components/tables/lesson-guide-table/writing'
import { eq } from 'ember-truth-helpers'
import { ProgressTable } from 'teacher-ui-client/components/tables/progress-table'
import { LayoutBlocksModal } from 'teacher-ui-client/components/layout-blocks/modal'
import { FormEditProgress } from 'teacher-ui-client/components/form/edit-progress'
import { array, fn, hash } from '@ember/helper'
import RouteTemplate from 'ember-route-template'
import { isProduct } from 'teacher-ui-client/utils/products'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { teacherViewedEvent } from 'teacher-ui-client/modifiers/teacher-viewed-event'

interface Signature {
  Args: {
    model: ModelFor<ManageProgressRoute>
    controller: ManageProgressController
  }
}

export const ManageProgressPage: TOC<Signature> = <template>
  <PageHeader>
    <:title>
      <h1>
        {{t "manage.manageProgress.title"}}
      </h1>
    </:title>
  </PageHeader>

  <p class="mb-3 text-center">
    {{t "manage.manageProgress.lessonGuide"}}
  </p>

  <div class="my-6 flex flex-wrap justify-center gap-6">
    {{#if (eq @model.subscriptionType SubscriptionType.Reading)}}
      <LessonGuideTableReading
        {{teacherViewedEvent
          "teacherViewed"
          "re"
          eventData=(hash teacherId=@model.currentTeacherId table="reading-lesson-guide")
        }}
        class="w-full max-w-4xl"
      />
      <LessonGuideTableFastPhonics
        {{teacherViewedEvent
          "teacherViewed"
          "fast-phonics"
          eventData=(hash teacherId=@model.currentTeacherId table="fast-phonics-lesson-guide")
        }}
        class="w-full max-w-md"
      />
    {{else if (eq @model.subscriptionType SubscriptionType.Maths)}}
      <LessonGuideTableMaths
        {{teacherViewedEvent
          "teacherViewed"
          "maths"
          eventData=(hash teacherId=@model.currentTeacherId table="maths-lesson-guide")
        }}
        class="w-full max-w-6xl"
      />
    {{else if (eq @model.subscriptionType SubscriptionType.Writing)}}
      <LessonGuideTableWriting
        {{teacherViewedEvent
          "teacherViewed"
          "writing"
          eventData=(hash teacherId=@model.currentTeacherId table="writing-lesson-guide")
        }}
        class="w-full max-w-5xl"
      />
    {{/if}}
  </div>

  <ProgressTable
    @exportable={{true}}
    @items={{@controller.students}}
    @allSchoolClasses={{@model.allSchoolClasses}}
    @updateProgressesTask={{@controller.updateProgressesTask}}
    @subscriptionType={{@model.subscriptionType}}
    @sortingConfig={{@controller.sortingConfig}}
    @paginationConfig={{@controller.paginationConfig}}
    @onSortClick={{@controller.updateSorts}}
    @onItemButtonClick={{@controller.itemPickAction}}
    @selectedItemIds={{@controller.selectedStudentIds}}
    @onItemSelect={{@controller.itemSelectAction}}
    @onItemSelectAll={{@controller.itemSelectAllAction}}
    @textFilter={{@controller.textFilter}}
    @setTextFilter={{@controller.setTextFilter}}
    @onBatchEditClick={{fn @controller.editProgressModal true}}
  />
  {{#if (isProduct @controller.product)}}
    <LayoutBlocksModal
      @title={{t "manage.manageProgress.bulkActions.editProgress.title"}}
      @showModal={{@controller.editProgressModalOpen}}
      @close={{@controller.onCancel}}
    >
      <FormEditProgress
        @product={{@controller.product}}
        @editStudents={{if @controller.pickedStudent (array @controller.pickedStudent) @controller.selectedStudents}}
        @onSuccess={{@controller.onSuccess}}
        @onCancel={{@controller.onCancel}}
      />

    </LayoutBlocksModal>
  {{/if}}
</template>

export default RouteTemplate(ManageProgressPage)
